import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useAccounts } from "../lib/account";
import { useTheme } from "../lib/theme";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { ChartLine, ListChecks, ChartBar, CreditCard, GearSix, SignOut, Lightning, Sun, Moon, Desktop, ChartLineUp, Trophy, Target, Rocket, List, X, Crown, Certificate, UserCircle } from "@phosphor-icons/react";
import PaywallListener from "./PaywallListener";
import MarketTicker from "./MarketTicker";
import HelpCenter from "./HelpCenter";
import TrialGateBanner from "./TrialGateBanner";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLine },
  { to: "/trades", label: "Trades", icon: ListChecks },
  { to: "/charts", label: "Charts", icon: ChartLineUp },
  { to: "/analytics", label: "Analytics", icon: ChartBar },
  { to: "/strategies", label: "Strategies", icon: Target },
  { to: "/prop", label: "Prop Firm", icon: Trophy },
  { to: "/prop-challenge", label: "Prop Challenge", icon: Rocket },
  { to: "/certificates", label: "Certificates", icon: Certificate },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: GearSix },
  { to: "/profile", label: "Profile", icon: UserCircle },
];

function ThemeToggle() {
  const { theme, cycle } = useTheme();
  const next = theme === "system" ? "light" : theme === "light" ? "dark" : "system";
  const nextLabel = next === "system" ? "System" : next === "light" ? "Light" : "Dark";
  const Icon = theme === "dark" ? Moon : theme === "light" ? Sun : Desktop;
  return (
    <Button variant="ghost" size="icon" onClick={cycle} data-testid="theme-toggle"
      title={`Theme: ${theme[0].toUpperCase() + theme.slice(1)} · click for ${nextLabel}`}
      aria-label={`Theme: ${theme}. Click to switch to ${nextLabel}.`}>
      <Icon size={16} />
    </Button>
  );
}

function SidebarBody({ accounts, activeId, setActive, onNav, isAdmin }) {
  const items = isAdmin ? [...nav, { to: "/admin", label: "Admin", icon: Crown }] : nav;
  return (
    <>
      <div className="px-6 py-6 border-b border-border">
        <Link to="/dashboard" onClick={onNav} className="flex items-center gap-2" data-testid="brand-link">
          <Lightning size={22} weight="fill" className="text-primary" />
          <span className="relative inline-block font-display text-xl font-bold tracking-tight leading-none pt-2">
            <span className="absolute right-0 -top-1 text-[7px] font-semibold tracking-[0.15em] uppercase text-primary/80 whitespace-nowrap">by Yash Khajania</span>
            Tradelog
          </span>
        </Link>
        <p className="text-[10px] tracking-[0.2em] text-muted-foreground uppercase mt-2">Trading Journal</p>
      </div>

      <div className="px-4 py-4 border-b border-border">
        <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Active Account</label>
        <Select value={activeId} onValueChange={setActive}>
          <SelectTrigger data-testid="account-switcher" className="mt-2 bg-secondary border-border">
            <SelectValue placeholder="Select account" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map(a => (
              <SelectItem key={a.id} value={a.id} data-testid={`account-option-${a.id}`}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} onClick={onNav} data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`
            }>
            <Icon size={18} weight="regular" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { accounts, activeId, setActive } = useAccounts();
  const nav_ = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop sidebar collapse — persisted so it survives navigation & reloads.
  // Mimics the Exness / TradingView pattern where the hamburger folds the
  // whole nav rail away and hands the width to the chart.
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try { return localStorage.getItem("tradelog.sidebarCollapsed") === "1"; }
    catch { return false; }
  });
  const toggleDesktop = () => {
    setDesktopCollapsed(v => {
      const next = !v;
      try { localStorage.setItem("tradelog.sidebarCollapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // Auto-close mobile menu on route change so the user can actually see the page.
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  // Lock body scroll when the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const doLogout = () => { logout(); nav_("/login"); };

  const isPro = user?.is_pro;
  const onTrial = user?.subscription?.status === "trial" && isPro;
  const paidActive = isPro && !onTrial;
  const trialEnd = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (86400000))) : 0;

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar — collapsible via the hamburger in the top bar */}
      <aside className={`hidden lg:flex shrink-0 border-r border-border bg-card/40 backdrop-blur-xl flex-col overflow-hidden transition-[width] duration-200 ease-out ${desktopCollapsed ? "w-0 border-r-0" : "w-64"}`}
        data-testid="sidebar-desktop"
        data-collapsed={desktopCollapsed ? "1" : "0"}>
        {!desktopCollapsed && (
          <SidebarBody accounts={accounts} activeId={activeId} setActive={setActive} isAdmin={!!user?.is_admin} />
        )}
      </aside>

      {/* Mobile drawer + overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-sidebar-overlay"
          aria-hidden="true" />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] border-r border-border bg-card flex flex-col transition-transform duration-200 ease-out lg:hidden ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar-mobile">
        <SidebarBody
          accounts={accounts}
          activeId={activeId}
          setActive={setActive}
          onNav={() => setMobileOpen(false)}
          isAdmin={!!user?.is_admin}
        />
      </aside>

      <main className="flex-1 min-w-0 relative flex flex-col">
        <PaywallListener />
        <MarketTicker />
        {/* Top-bar: hamburger + brand on mobile, user chip on the right always */}
        <div className="sticky top-0 z-30 flex items-center gap-3 px-3 sm:px-4 lg:px-8 py-3 border-b border-border bg-background/85 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Open menu"
            className="lg:hidden -ml-1 shrink-0"
            data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={18} weight="bold" /> : <List size={20} weight="bold" />}
          </Button>
          {/* Desktop hamburger — collapses/expands the whole sidebar rail
              (Exness / TradingView style). */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDesktop}
            aria-label={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden lg:inline-flex -ml-1 shrink-0"
            data-testid="desktop-sidebar-toggle">
            <List size={20} weight="bold" />
          </Button>
          <Link to="/dashboard" className="lg:hidden flex items-center gap-1.5 min-w-0" data-testid="mobile-brand">
            <Lightning size={16} weight="fill" className="text-primary shrink-0" />
            <span className="relative inline-block font-display font-bold tracking-tight leading-none pt-1.5 truncate">
              <span className="absolute right-0 -top-1 text-[6px] font-semibold tracking-[0.15em] uppercase text-primary/80 whitespace-nowrap">by Yash Khajania</span>
              Tradelog
            </span>
          </Link>
          <div className="flex-1" />
          {onTrial && trialDaysLeft > 0 && (
            <Link to="/billing" data-testid="topbar-upgrade-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 hover:opacity-90 transition-opacity">
              <Lightning size={12} weight="fill" />
              Upgrade — {trialDaysLeft}d trial left
            </Link>
          )}
          {!isPro && (
            <Link to="/billing" data-testid="topbar-subscribe-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-red-500 text-white text-xs font-medium px-3 py-1.5 hover:opacity-90 transition-opacity">
              Subscribe
            </Link>
          )}
          {paidActive && (
            <span className="hidden sm:inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-xs font-medium px-3 py-1.5">
              <Lightning size={12} weight="fill" />
              {user?.subscription?.plan_id || "Pro"}
            </span>
          )}
          <div className="flex items-center gap-2 rounded-full border border-border bg-card px-2 py-1 shrink-0"
            data-testid="topbar-user-chip">
            <div className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="hidden md:block max-w-[140px] min-w-0">
              <p className="text-xs font-medium truncate leading-tight">{user?.name || "User"}</p>
              <p className="text-[10px] text-muted-foreground truncate leading-tight">{user?.email}</p>
            </div>
            <div className="w-px h-6 bg-border mx-1 hidden md:block" />
            <Link to="/profile" data-testid="topbar-profile-link">
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Profile">
                <UserCircle size={15} />
              </Button>
            </Link>
            <ThemeToggle />
            <Button data-testid="topbar-logout-btn" variant="ghost" size="icon" onClick={doLogout} className="h-7 w-7">
              <SignOut size={14} />
            </Button>
          </div>
        </div>
        <div className="flex-1 min-w-0 overflow-x-hidden">
          <TrialGateBanner />
          <Outlet />
        </div>
      </main>
      <HelpCenter />
    </div>
  );
}
